import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./ClubRuleSingle.module.scss";

const STATUSES = ["active", "inactive"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

const IMAGE_SLOTS = [
  {
    key: "desktopOne",
    label: "Desktop One",
    presignPath: "desktop-one",
    keyField: "ruleDesktopOneKey",
  },
  {
    key: "desktopTwo",
    label: "Desktop Two",
    presignPath: "desktop-two",
    keyField: "ruleDesktopTwoKey",
  },
  {
    key: "desktopHalfLeft",
    label: "Desktop Half Left",
    presignPath: "desktop-half-left",
    keyField: "ruleDesktopHalfLeftKey",
  },
  {
    key: "desktopHalfRight",
    label: "Desktop Half Right",
    presignPath: "desktop-half-right",
    keyField: "ruleDesktopHalfRightKey",
  },

  {
    key: "mobileOne",
    label: "Mobile One",
    presignPath: "mobile-one",
    keyField: "ruleMobileOneKey",
  },
  {
    key: "mobileTwo",
    label: "Mobile Two",
    presignPath: "mobile-two",
    keyField: "ruleMobileTwoKey",
  },
  {
    key: "mobileHalfLeft",
    label: "Mobile Half Left",
    presignPath: "mobile-half-left",
    keyField: "ruleMobileHalfLeftKey",
  },
  {
    key: "mobileHalfRight",
    label: "Mobile Half Right",
    presignPath: "mobile-half-right",
    keyField: "ruleMobileHalfRightKey",
  },
];

const ClubRuleSingle = () => {
  const { cityId, streetId, clubId, clubRuleId } = useParams();
  const navigate = useNavigate();

  const [club, setClub] = useState(null);
  const [rule, setRule] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  const [uploadErr, setUploadErr] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");

  const [form, setForm] = useState({
    sortOrder: 1,
    status: "active",
    title: "",
    description: "",
    footer: "",
  });

  const [imageFiles, setImageFiles] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});

  const backToRulesPath = useMemo(() => {
    if (!cityId || !streetId || !clubId) return "/admin/cities";
    return `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}/rules`;
  }, [cityId, streetId, clubId]);

  const hydrateForm = (doc) => {
    setForm({
      sortOrder: Number.isFinite(Number(doc?.sortOrder))
        ? Number(doc.sortOrder)
        : 1,
      status: doc?.status || "active",
      title: doc?.title || "",
      description: doc?.description || "",
      footer: doc?.footer || "",
    });
  };

  const fetchClubMeta = useCallback(async () => {
    if (!cityId || !streetId || !clubId) return;
    try {
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`,
      );
      setClub(data || null);
    } catch (e) {
      console.error("Failed to load club meta", e);
      setClub(null);
    }
  }, [cityId, streetId, clubId]);

  const fetchRule = useCallback(async () => {
    if (!cityId || !streetId || !clubId || !clubRuleId) return;

    try {
      setLoading(true);
      setErr("");
      setSuccess("");
      setUploadErr("");
      setUploadMsg("");

      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/rules/${clubRuleId}`,
      );

      setRule(data || null);
      if (data) hydrateForm(data);
    } catch (e) {
      console.error("Failed to load rule", e);
      setErr(
        e?.response?.data?.message || e?.message || "Could not load rule.",
      );
      setRule(null);
    } finally {
      setLoading(false);
    }
  }, [cityId, streetId, clubId, clubRuleId]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchClubMeta(), fetchRule()]);
  }, [fetchClubMeta, fetchRule]);

  useEffect(() => {
    refreshAll();

    return () => {
      Object.values(imagePreviews || {}).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          // Intentionally ignored: object URL cleanup should not block leaving this page.
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clubName = club?.clubName || "Club";

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFileChange = (slotKey, file) => {
    setUploadErr("");
    setUploadMsg("");

    setImageFiles((p) => ({ ...p, [slotKey]: file || null }));

    setImagePreviews((p) => {
      const prev = p[slotKey];
      if (prev) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          // Intentionally ignored: object URL cleanup should not block replacing the preview.
        }
      }

      if (!file) {
        const copy = { ...p };
        delete copy[slotKey];
        return copy;
      }

      return { ...p, [slotKey]: URL.createObjectURL(file) };
    });
  };

  const uploadSlot = async (slotKey, presignPath) => {
    const file = imageFiles[slotKey];
    if (!file) {
      setUploadErr("Select a file first.");
      return;
    }

    try {
      setUploadingKey(slotKey);
      setUploadErr("");
      setUploadMsg("");
      setErr("");
      setSuccess("");

      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const contentType = file.type || "application/octet-stream";

      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/rules/${clubRuleId}/presign/${presignPath}`,
        { ext, contentType },
      );

      const uploadUrl = data?.uploadUrl;
      if (!uploadUrl) throw new Error("No uploadUrl returned.");

      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });

      if (!res.ok) throw new Error("Upload failed.");

      setUploadMsg(`${slotKey} uploaded.`);
      await fetchRule();
    } catch (e) {
      setUploadErr(
        e?.response?.data?.message || e?.message || "Upload failed.",
      );
    } finally {
      setUploadingKey("");
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setUploadErr("");
    setUploadMsg("");

    if (!form.title.trim()) {
      setErr("Title is required.");
      return;
    }

    const sortNum = Number(form.sortOrder);
    if (!Number.isInteger(sortNum) || sortNum < 1) {
      setErr("Rule Number must be a positive whole number (1, 2, 3...).");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        sortOrder: sortNum,
        status: form.status,
        title: form.title.trim(),
        description: form.description,
        footer: form.footer,
      };

      const { data } = await api.patch(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/rules/${clubRuleId}`,
        payload,
      );

      setRule(data || null);
      if (data) hydrateForm(data);

      setSuccess("Saved.");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !rule) {
    return <div className={cls.page}>Loading…</div>;
  }

  if (!rule) {
    return (
      <div className={cls.page}>
        <header className={cls.header}>
          <button className={cls.backBtn} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1 className={cls.title}>Rule</h1>
          <p className={cls.subtitle}>Not found.</p>
        </header>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.leftHead}>
          <Link className={cls.backLink} to={backToRulesPath}>
            ← Back to Rules
          </Link>

          <p className={cls.eyebrow}>Admin · Club Rules</p>
          <h1 className={cls.title}>{clubName} · Rule</h1>
          <p className={cls.subtitle}>Edit rule content and upload images.</p>
        </div>

        <div className={cls.actionsTop}>
          <button
            type="button"
            className={cls.ghostBtn}
            onClick={refreshAll}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </header>

      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Rule #</span>
          <span className={cls.metaValue}>{rule.sortOrder ?? "—"}</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Title</span>
          <span className={cls.metaValue}>{rule.title || "Untitled Rule"}</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{rule.status || "active"}</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Updated</span>
          <span className={cls.metaValue}>
            {formatDate(rule.lastUpdatedAt || rule.updatedAt)}
          </span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {success && <div className={cls.success}>{success}</div>}
      {uploadErr && <div className={cls.error}>⚠ {uploadErr}</div>}
      {uploadMsg && <div className={cls.success}>{uploadMsg}</div>}

      <div className={cls.layout}>
        {/* LEFT: Images */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>Rule Images</h2>
            <p className={cls.cardSubtitle}>
              Pick a file, preview it, then upload.
            </p>
          </div>

          <div className={cls.imagesGrid}>
            {IMAGE_SLOTS.map((slot) => {
              const picked = imagePreviews[slot.key];
              const serverUrl = rule?.[slot.key]?.url || null;
              const previewUrl = picked || serverUrl || null;
              const file = imageFiles[slot.key];

              return (
                <div key={slot.key} className={cls.imageSlot}>
                  <p className={cls.imageLabel}>{slot.label}</p>

                  <div className={cls.imagePreviewMini}>
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={slot.label}
                        className={cls.miniImage}
                      />
                    ) : (
                      <div className={cls.miniFallback}>No image</div>
                    )}
                  </div>

                  <label className={cls.fileLabel}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleFileChange(slot.key, e.target.files?.[0] || null)
                      }
                    />
                    <span className={cls.fileLabelText}>
                      {file ? "Change file" : "Choose file"}
                    </span>
                  </label>

                  {file?.name && (
                    <div className={cls.fileName}>{file.name}</div>
                  )}

                  <button
                    type="button"
                    className={cls.avatarBtn}
                    onClick={() => uploadSlot(slot.key, slot.presignPath)}
                    disabled={uploadingKey === slot.key || !file}
                  >
                    {uploadingKey === slot.key ? "Uploading…" : "Upload"}
                  </button>

                  <div className={cls.keysList}>
                    <strong>Key</strong>
                    <code>{rule?.[slot.keyField] || "—"}</code>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* RIGHT: Form */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>Rule Content</h2>
            <p className={cls.cardSubtitle}>Update the text content.</p>
          </div>

          <form className={cls.form} onSubmit={onSave}>
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label}>Rule Number</label>
                <input
                  className={cls.input}
                  type="number"
                  min={1}
                  value={form.sortOrder}
                  onChange={(e) => setField("sortOrder", e.target.value)}
                  placeholder="1"
                />
                <p className={cls.help}>
                  Controls the order rules appear (lower shows first).
                </p>
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Status</label>
                <select
                  className={cls.select}
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <p className={cls.help}>
                  Active shows to members. Inactive is draft.
                </p>
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Title *</label>
                <input
                  className={cls.input}
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Respect & Consent"
                  required
                />
              </div>
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Description</label>
              <textarea
                className={cls.input}
                rows={6}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Write the full rule here…"
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label}>Footer</label>
              <textarea
                className={cls.input}
                rows={3}
                value={form.footer}
                onChange={(e) => setField("footer", e.target.value)}
                placeholder="Short footer note, disclaimer, or reminder…"
              />
            </div>

            <div className={cls.actions}>
              <button
                className={cls.secondaryBtn}
                type="button"
                onClick={() => navigate(backToRulesPath)}
                disabled={saving}
              >
                Back
              </button>

              <button
                className={cls.primaryBtn}
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ClubRuleSingle;
